<template>
    <b-row :style="showButtonOnly ? 'display:inline-block' : ''">
        <b-col>
            <div v-if="title" class="d-flex justify-content-between align-items-center mb-2 mt-n2">
                <h4 class="card-subtitle mb-0 mt-0">{{ title }}</h4>
                <b-button size="sm" v-if="!readOnly" variant="info" v-b-modal="`Insurance-Add-Modal-${id}`" :disabled="disabled || !isPatientSaved">Add Plan</b-button>
            </div>
            <b-button v-if="!readOnly && !title" variant="info" v-b-modal="`Insurance-Add-Modal-${id}`" :disabled="disabled || !isPatientSaved">Add Plan</b-button>

            <small v-if="!readOnly && !isPatientSaved" class="text-muted d-block mt-1">
                Save the patient before adding a plan.
            </small>

            <div v-if="readOnly && inactivePlanCount > 0 && !showButtonOnly" class="readonly-controls mb-2">
                <b-form-checkbox v-model="showInactive" size="sm" inline>
                    Show Inactive ({{ inactivePlanCount }})
                </b-form-checkbox>
            </div>

            <Draggable v-if="insuranceInfos?.length && !showButtonOnly" v-model="insuranceInfos" draggable=".item"
                handle=".handle" @change="reorderPlans">
                <transition-group name="fade">
                    <b-card v-for="(p, i) in insuranceInfos" :key="p.patientPlan.id" class="item"
                        v-show="isPlanVisible(p, i)">
                        <div class="d-flex align-items-center">
                            <div v-if="!readOnly" class="align-self-stretch d-flex flex-column">
                                <div class="handle flex-fill">
                                    <i class="fas fa-grip-horizontal" />
                                </div>
                                <div class="move-top" v-if="i > 0"
                                    @click="reorderPlans({ moved: { element: p, newIndex: 0, oldIndex: i } })"
                                    title="Move to Top">
                                    <i class="fas fa-arrow-up" />
                                </div>
                            </div>

                            <div class="flex-fill plan-name">
                                <div v-if="p.plan.name">
                                    <strong>{{ p.plan.name }}</strong>
                                </div>
                                <div v-if="p.plan.bin">
                                    <small>BIN: {{ p.plan.bin }}</small>
                                </div>
                                <div v-if="p.plan.pcn">
                                    <small>PCN: {{ p.plan.pcn }}</small>
                                </div>
                                <div v-if="p.plan.phoneNumber">
                                    <small>Phone: {{ p.plan.phoneNumber }}</small>
                                </div>
                            </div>

                            <div class="text-muted plan-details">
                                <div v-if="p.patientPlan?.groupId">
                                    <small>Group Id: {{ p.patientPlan.groupId }}</small>
                                </div>
                                <div v-if="p.patientPlan?.cardholderId">
                                    <small>Cardholder Id: {{ p.patientPlan.cardholderId }}</small>
                                </div>
                                <div v-if="p.patientPlan?.relationshipCode">
                                    <small>Relationship Code: {{ p.patientPlan.relationshipCode }}</small>
                                </div>
                            </div>

                            <div v-if="!readOnly" title="Active">
                                <b-form-checkbox switch v-model="p.patientPlan.active"
                                    @change="updatePlanActivity(p)" />
                            </div>
                            <div v-else>
                                <b-badge :variant="p.patientPlan.active ? 'success' : 'secondary'">
                                    {{ p.patientPlan.active ? 'Active' : 'Inactive' }}
                                </b-badge>
                            </div>
                        </div>
                    </b-card>
                </transition-group>
            </Draggable>

            <div v-if="readOnly && collapsedCount > 0" class="mt-1 text-center">
                <a href="#" class="show-more-link" @click.prevent="showAllPlans = !showAllPlans">
                    {{ showAllPlans ? 'Show less' : `Show ${collapsedCount} more...` }}
                </a>
            </div>

            <div v-if="readOnly && insuranceInfos?.length && !visiblePlans.length && !showButtonOnly" class="text-muted mt-1">
                No active insurance plans. Check "Show Inactive" to view all plans.
            </div>

            <div v-if="!insuranceInfos?.length" v-show="!showButtonOnly">
                No registered insurance plans for this patient.
            </div>
        </b-col>
        <b-modal v-if="!readOnly" :id="`Insurance-Add-Modal-${id}`" size="lg" no-close-on-backdrop @hide="clear" title="Plan Info">
            <b-alert v-if="parsedInsuranceData" show variant="info" class="mb-3">
                <i class="fas fa-robot"></i> AI parsed data has been pre-filled. Please review and edit if needed.
            </b-alert>
            <SearchComponent v-model="addedPlan" search-u-r-l="/Plan" label="Plan (BIN/PCN/Name/PBM)"
                createNewLabel="Create New Plan" createNewObject :id="addedPlan && addedPlan.id"
                @createNewObject="showPlanForm" :prepopulate="prePopulatePlanString != ''"
                :params="{ prepopulateSearchTerm: prePopulatePlanString, providerType: eligibilityProviderType }"
                :autoselect="prePopulatePlanString != ''">
            </SearchComponent>
            <b-form-row class="py-2">
                <b-col>
                    <b-form-group label="Cardholder Id" label-align="center">
                        <b-form-input v-model="addedPatientPlan.cardholderId"
                            :disabled="isInventoryAvailable"></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Group Id" label-align="center">
                        <b-form-input v-model="addedPatientPlan.groupId"
                            :disabled="isInventoryAvailable"></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Person Code" label-align="center">
                        <b-form-input v-model="addedPatientPlan.personCode"
                            :disabled="isInventoryAvailable"></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Relationship Code" label-align="center" class="relationship-code">
                        <EnumSelector v-model="addedPatientPlan.relationshipCode" :disabled="isInventoryAvailable"
                            :enumCodes="relationshipCodes">
                        </EnumSelector>
                    </b-form-group>
                </b-col>
            </b-form-row>
            <b-row v-if="isInventoryAvailable">
                <b-col cols="12">
                    <b>Codes in inventory: </b> {{ addedPlan.inventoryQuantityAvailable }}
                </b-col>
                <b-col cols="12">
                    <div class="alert alert-info" role="alert">
                        <b>Cardholder Id, Group Id, Person Code, Relationship Code</b> will be assigned
                        automatically from the Plan Codes Inventory.
                    </div>
                </b-col>
            </b-row>
            <form v-if="false">
                <b-form-row>
                    <fg-input v-model="addedPatientPlan.groupId" label='Group Id' class="ml-2"></fg-input>
                    <fg-input v-model="addedPatientPlan.cardholderId" label='Cardholder Id' class="ml-2"></fg-input>
                    <EnumSelector v-model="addedPatientPlan.relationshipCode" label="Relationship Code"
                        :enum-codes="relationshipCodes" class="float-right">
                    </EnumSelector>
                </b-form-row>
            </form>
            <PlanDetailsPage v-show="showNewPlan" :create-new-plan="true" :buttonTitles="planButtonTitles"
                :parsedPlanData="parsedPlanDataForCreation"
                @created="onPlanCreated"></PlanDetailsPage>

            <template #modal-footer="{ cancel }">
                <div class="d-flex flex-fill">
                    <div class="flex-fill">
                        <b-button @click="cancel">Cancel</b-button>
                    </div>
                    <div class="d-flex" style="gap: 8px;">
                        <b-button variant="outline-primary" :disabled="!isPatientSaved" @click="applyPlansToPatient(true)">
                            Add as Primary
                        </b-button>
                        <b-button variant="primary" :disabled="!isPatientSaved" @click="applyPlansToPatient(false)">
                            Add
                        </b-button>
                    </div>
                </div>
            </template>
        </b-modal>
    </b-row>
</template>

<script lang="ts">
import { Component, Prop, VModel, Vue, Watch } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import { BillingFlags, Plan } from '@/models/Plan';
import { PatientPlan, RelationshipCode } from '@/models/PatientPlan';
import Axios, { AxiosError } from 'axios';
import PlanDetailsPage from '@/pages/Plan/PlanDetailsPage.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { CombinedPlan } from "@/models/CombinedPlan";
import Draggable from 'vuedraggable';
import { deriveProviderType, getInsuranceDisplayName, ParsedInsuranceData } from '@/models/ParsedInsuranceData';

@Component({
    components: {
        PlanDetailsPage,
        SearchComponent,
        EnumSelector,
        Draggable
    }
})
export default class PatientPlansComponent extends Vue {
    @VModel({ type: Patient, default: new Patient() }) private patient!: Patient;
    @Prop({ default: false }) private showButtonOnly!: boolean;
    @Prop({ default: false }) private disabled!: boolean;
    @Prop({ default: false, type: Boolean }) private readOnly!: boolean;
    @Prop({ default: null }) private addTemplate!: CombinedPlan;
    @Prop({ default: "", type: String }) private id!: string;
    @Prop({ default: "", type: String }) private title!: string;
    @Prop({ default: null }) private parsedInsuranceData: ParsedInsuranceData | null;
    private showNewPlan = false;
    private prePopulatePlanString: string = "";
    private eligibilityProviderType: number | null = null;

    private insuranceInfos: CombinedPlan[] = [];
    private showInactive = false;
    private showAllPlans = false;
    private readonly maxVisiblePlans = 2;
    private addedPlan: Plan = new Plan();
    private addedPatientPlan: PatientPlan = new PatientPlan();
    private relationshipCodes = RelationshipCode;
    private originalAIParsedValues: Record<string, any> | null = null;
    // private planFields: any[] = [
    //     { key: "plan.name", label: "Name", },
    //     { key: "plan.bin", label: "BIN", },
    //     { key: "plan.pcn", label: "PCN", },
    //     { key: "patientPlan.memberId", label: "Member Id", },
    //     { key: "patientPlan.groupId", label: "Group Id", },
    //     { key: "patientPlan.cardholderId", label: "Cardholder Id", },
    //     { key: "patientPlan.relationshipCode", label: "Relationship Code", },
    //     { key: "patientPlan.active", label: "Active", },
    // ];
    private planButtonTitles = {
        new: "",
        clear: "",
        save: "Save Changes",
        cancel: ""
    };

    @Watch("addTemplate")
    addTemplateUpdated(val: CombinedPlan, oldVal: any) {
        if (!val) return;

        console.log("addTemplate: ", val, oldVal);

        this.addedPatientPlan = new PatientPlan(val.patientPlan);
        this.addedPatientPlan.relationshipCode = RelationshipCode.Cardholder;
        this.addedPlan = new Plan(val.plan);
        this.eligibilityProviderType = val.plan.providerType || null;
        this.prePopulatePlanString = `${val.plan.bin} ${val.plan.pcn}`;
        this.$bvModal.show(`Insurance-Add-Modal-${this.id}`);
    }

    @Watch("addedPlan")
    addedPlanUpdate(val: Plan) {
        //Hide new Plan form if a existing one was selected.
        if (!val.id) return;

        this.showNewPlan = false;

        if (!this.isInventoryAvailable) {
            this.addedPatientPlan.groupId = this.addedPatientPlan.groupId || val.defaultGroupId;
            this.addedPatientPlan.cardholderId = this.addedPatientPlan.cardholderId || val.defaultCardholderId;
            this.addedPatientPlan.personCode = this.addedPatientPlan.personCode || val.defaultPersonCode;
            this.addedPatientPlan.relationshipCode = val.defaultRelationshipCode;
        } else {
            this.addedPatientPlan.groupId = '';
            this.addedPatientPlan.cardholderId = '';
            this.addedPatientPlan.personCode = '';
            this.addedPatientPlan.relationshipCode = 0;
        }
    }

    mounted() {
        this.fetchPlans();
        this.applyParsedInsuranceData();
    }

    @Watch('parsedInsuranceData')
    onParsedDataChanged() {
        this.applyParsedInsuranceData();
    }

    applyParsedInsuranceData() {
        if (!this.parsedInsuranceData) return;

        const parsed = this.parsedInsuranceData;

        // Store original AI values for change tracking - ALL fields
        this.originalAIParsedValues = {
            bin: parsed.bin,
            pcn: parsed.pcn,
            groupId: parsed.groupId,
            cardholderId: parsed.cardholderId,
            insuranceCompanyName: getInsuranceDisplayName(parsed.insuranceCompanyName),
            providerType: deriveProviderType(parsed.isMedicaid, parsed.isMedicare),
            address1: parsed.claimAddress?.street,
            addressCity: parsed.claimAddress?.city,
            addressState: parsed.claimAddress?.state,
            addressZip: parsed.claimAddress?.postalCode,
            defaultGroupId: parsed.groupId,
            defaultCardholderId: parsed.cardholderId
        };

        // Pre-fill the search with BIN and PCN
        if (this.parsedInsuranceData.bin || this.parsedInsuranceData.pcn) {
            const searchTerms = [];
            if (this.parsedInsuranceData.bin) searchTerms.push(this.parsedInsuranceData.bin);
            if (this.parsedInsuranceData.pcn) searchTerms.push(this.parsedInsuranceData.pcn);
            this.prePopulatePlanString = searchTerms.join(' ');
        }

        // Pre-fill patient plan fields
        if (this.parsedInsuranceData.groupId) {
            this.addedPatientPlan.groupId = this.parsedInsuranceData.groupId;
        }
        if (this.parsedInsuranceData.cardholderId) {
            this.addedPatientPlan.cardholderId = this.parsedInsuranceData.cardholderId;
        }

        // Default relationship code to Cardholder if not set
        if (!this.addedPatientPlan.relationshipCode) {
            this.addedPatientPlan.relationshipCode = RelationshipCode.Cardholder;
        }
    }

    clear() {
        this.addedPatientPlan = new PatientPlan();
        this.addedPlan = new Plan();
        this.originalAIParsedValues = null;
        this.eligibilityProviderType = null;
        this.$emit('hide');
    }

    @Watch('showInactive')
    onShowInactiveChanged() {
        this.showAllPlans = false;
    }

    @Watch('patient')
    async fetchPlans() {
        if (!this.patient.id) return;
        this.showAllPlans = false;
        this.showInactive = false;

        try {
            const response = await Axios.get(`insurance/${this.patient.id}`);
            this.processPlanData(response.data);
        } catch (error) {
            console.error("Error while loading the insurance plans", { error, response: (error as AxiosError)?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    processPlanData(data: { plans: Array<Plan>, patientPlans: Array<PatientPlan> }) {
        this.insuranceInfos = data.plans.map((item: any, i: number) => {
            return { plan: item, patientPlan: data.patientPlans[i] } as CombinedPlan;
        });
    }

    async updatePlanActivity(p: CombinedPlan) {
        try {
            const resp = await Axios.post<CombinedPlan[]>("/Plan/UpdatePatientPlan", p.patientPlan);

            this.insuranceInfos = resp.data;
            this.$emit("plan-saved");
        } catch (err) {
            console.error("Error while updating patient plan activity.", { err, response: (err as AxiosError)?.response });
            this.$notification(NotificationOptions.error(err));
        }
    }

    async applyPlansToPatient(isPrimary : boolean) {
        this.addedPatientPlan.patientId = this.patient.id;
        this.addedPatientPlan.planId = this.addedPlan.id;
        this.addedPatientPlan.relationshipCode = Number(this.addedPatientPlan.relationshipCode);

        this.addedPatientPlan.rank = isPrimary ? 0 : null;

        try {
            await Axios.post(`/Plan/AssignToPatient`, this.addedPatientPlan);

            this.$notification(NotificationOptions.notificationOptionsPreset("Plan was assigned to patient successfully.", NotificationOptions.NotificationTypes.success));
            this.$emit('plan-saved');

            // Emit tracking data if AI parsed data was available
            if (this.originalAIParsedValues) {
                const trackingData = this.calculateTrackingData();
                this.$emit('insurance-processed', trackingData);
            }

            this.fetchPlans();
            this.addedPatientPlan = new PatientPlan();
            this.addedPlan = new Plan();

            this.$bvModal.hide(`Insurance-Add-Modal-${this.id}`);
        } catch (error) {
            console.error("Error while assigning plan to patient.", { error, response: (error as AxiosError)?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    calculateTrackingData(): { aiParsedDataUsed: boolean, aiParsedDataEdited: boolean } {
        if (!this.originalAIParsedValues) {
            return { aiParsedDataUsed: false, aiParsedDataEdited: false };
        }

        const original = this.originalAIParsedValues;

        // Check ALL fields for changes
        const wasEdited =
            // Plan identification fields
            (original.bin !== this.addedPlan.bin) ||
            (original.pcn !== this.addedPlan.pcn) ||
            (original.insuranceCompanyName !== this.addedPlan.name) ||
            (original.providerType !== this.addedPlan.providerType) ||
            // Address fields
            (original.address1 !== this.addedPlan.address1) ||
            (original.addressCity !== this.addedPlan.addressCity) ||
            (original.addressState !== this.addedPlan.addressState) ||
            (original.addressZip !== this.addedPlan.addressZip) ||
            // Default values
            (original.defaultGroupId !== this.addedPlan.defaultGroupId) ||
            (original.defaultCardholderId !== this.addedPlan.defaultCardholderId) ||
            // PatientPlan fields
            (original.groupId !== this.addedPatientPlan.groupId) ||
            (original.cardholderId !== this.addedPatientPlan.cardholderId);

        return {
            aiParsedDataUsed: true,
            aiParsedDataEdited: wasEdited
        };
    }

    showPlanForm() {
        this.showNewPlan = true;
        this.addedPlan = new Plan();
    }

    onPlanCreated(newPlan: Plan) {
        this.addedPlan = newPlan;
        this.showNewPlan = false;
    }

    get visiblePlans(): CombinedPlan[] {
        if (!this.readOnly) return this.insuranceInfos;
        return this.insuranceInfos.filter(p => this.showInactive || p.patientPlan.active);
    }

    get inactivePlanCount(): number {
        return this.insuranceInfos?.filter(p => !p.patientPlan.active).length ?? 0;
    }

    get collapsedCount(): number {
        if (!this.readOnly || this.showInactive) return 0;
        const visible = this.visiblePlans.length;
        return visible > this.maxVisiblePlans && !this.showAllPlans
            ? visible - this.maxVisiblePlans
            : 0;
    }

    isPlanVisible(p: CombinedPlan, index: number): boolean {
        if (!this.readOnly) return true;
        // Hide inactive plans unless showInactive is checked
        if (!this.showInactive && !p.patientPlan.active) return false;
        // Show all plans when showInactive is checked (no collapse)
        if (this.showInactive) return true;
        // Collapse plans beyond the max visible count
        const visibleIndex = this.visiblePlans.findIndex(vp => vp.patientPlan.id === p.patientPlan.id);
        if (!this.showAllPlans && visibleIndex >= this.maxVisiblePlans) return false;
        return true;
    }

    get isPatientSaved(): boolean {
        return (this.patient?.id ?? 0) > 0;
    }

    get isInventoryAvailable(): boolean {
        const hasInventory = this.addedPlan.inventoryQuantityAvailable > 0;
        const isConnectiveRx = (this.addedPlan.flags & BillingFlags.ConnectiveRx) != 0;
        const isArcutisCopay = (this.addedPlan.flags & BillingFlags.ArcutisCopay) != 0;
        return hasInventory || isConnectiveRx || isArcutisCopay;
    }

    get parsedPlanDataForCreation(): Partial<Plan> | null {
        if (!this.parsedInsuranceData && !this.eligibilityProviderType) return null;

        const planData: Partial<Plan> = {};

        if (this.parsedInsuranceData) {
            const parsed = this.parsedInsuranceData;

            if (parsed.bin) planData.bin = parsed.bin;
            if (parsed.pcn) planData.pcn = parsed.pcn;

            const displayName = getInsuranceDisplayName(parsed.insuranceCompanyName);
            if (displayName) planData.name = displayName;

            planData.providerType = deriveProviderType(parsed.isMedicaid, parsed.isMedicare);

            if (parsed.claimAddress) {
                if (parsed.claimAddress.street) planData.address1 = parsed.claimAddress.street;
                if (parsed.claimAddress.city) planData.addressCity = parsed.claimAddress.city;
                if (parsed.claimAddress.state) planData.addressState = parsed.claimAddress.state;
                if (parsed.claimAddress.postalCode) planData.addressZip = parsed.claimAddress.postalCode;
            }
        }

        if (this.eligibilityProviderType) {
            planData.providerType = this.eligibilityProviderType;
        }

        return planData;
    }

    async reorderPlans(data: { moved: { element: CombinedPlan, newIndex: number, oldIndex: number } }) {
        const moved = data.moved;

        try {
            const updatedPlans = await Axios.post('/Plan/move-patient-plan-priority', {
                ...moved.element.patientPlan,
                rank: moved.newIndex
            });
            this.processPlanData(updatedPlans.data);
        } catch (error) {
            console.error("Error while reordering the insurance plan", { error, response: (error as AxiosError)?.response });
            this.$notification(NotificationOptions.error(error));
            this.fetchPlans();
        }
    }
}
</script>

<style scoped lang="scss">
.card {
    box-shadow: none !important;
    margin-bottom: 8px;
}

::v-deep(.card-body) {
    padding: 10px !important;
}

::v-deep(.card-body .d-flex) {
    gap: 8px;
}

.handle {
    cursor: grab;
}

.plan-name {
    width: 50%;
}

.plan-details {
    width: 50%;
}

.move-top {
    cursor: pointer;
    transition: all .3s;
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
    transition: all 0.25s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(30px, 0);
}

.fade-leave-active {
    position: absolute;
}

::v-deep(.relationship-code legend) {
    padding-bottom: 0;
}

.readonly-controls {
    display: flex;
    align-items: center;
}

.show-more-link {
    font-size: 0.85rem;
    color: #007bff;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}
</style>
